import { isSlackMessage, splitAddresses } from '@littlebird/action-cards';
import { stripRoutinePreamble } from '@littlebird/markdown-utils';
import { Marked } from 'marked';

import type { WaitingForYouListItem } from '@/features/home/use-waiting-for-you';
import { normalizeFindText, readFindText } from '@/lib/find-text';

export type FeedItem = WaitingForYouListItem;

export type FeedSearchField = {
  key: string;
  text: string;
  format?: 'html' | 'markdown' | 'text';
};

export function getFeedSearchFields(item: FeedItem): FeedSearchField[] {
  if (item.kind === 'chat_prompt') {
    return [
      { key: 'summary', text: item.entry.content.title },
      { key: 'action', text: 'Start chat' },
      { key: 'body', text: item.entry.content.body ?? '' },
    ];
  }
  const { decoded } = item.entry;
  const subject = decoded.summary || decoded.actionLabel;
  const fields: FeedSearchField[] = [{ key: 'summary', text: subject }];
  if (decoded.actionLabel !== subject)
    fields.push({ key: 'action', text: decoded.actionLabel });
  const parent = isSlackMessage(decoded.content)
    ? decoded.content.parent_message
    : undefined;
  const quote = parent?.text.trim() ?? '';
  const context = decoded.context?.trim() ?? '';
  if (quote) fields.push({ key: 'quote', text: quote });
  if (context && context !== quote)
    fields.push({ key: 'context', text: context });
  for (const field of decoded.fields) {
    if (field.advanced || field.key === 'account_label' || !field.value)
      continue;
    if (parent && field.key === 'channel') continue;
    if (
      !field.editable &&
      ['channel', 'title', 'target'].includes(field.key) &&
      field.value.trim().replace(/^#/, '') === subject.trim().replace(/^#/, '')
    )
      continue;
    if (
      decoded.content.type === 'google_calendar_event' &&
      field.key === 'attendees'
    ) {
      splitAddresses(field.value).forEach((text, index) => {
        fields.push({ key: `field:${field.key}:${index}`, text });
      });
    } else {
      fields.push({
        key: `field:${field.key}`,
        text:
          field.key === 'channel' ? field.value.replace(/^#/, '') : field.value,
        format: field.format,
      });
    }
  }
  return fields;
}

export type FeedSearchCache = WeakMap<
  FeedItem['entry']['card'],
  { source: FeedSearchField[]; fields: FeedSearchField[] }
>;

const emptyFields: FeedSearchField[] = [];

export function sameFeedSearchFields(
  left: FeedSearchField[],
  right: FeedSearchField[]
): boolean {
  return (
    left.length === right.length &&
    left.every((field, index) => {
      const other = right[index];
      return (
        field.key === other.key &&
        field.text === other.text &&
        field.format === other.format
      );
    })
  );
}

export function* indexFeedItems(
  items: readonly FeedItem[],
  cache: FeedSearchCache,
  isExcluded: (item: FeedItem) => boolean
): Generator<boolean, FeedSearchField[][]> {
  const rows: FeedSearchField[][] = [];
  for (const item of items) {
    if (isExcluded(item)) {
      rows.push(emptyFields);
    } else {
      const source = getFeedSearchFields(item);
      const cached = cache.get(item.entry.card);
      // Field values are the revision: local edits need not change the sync id
      const unchanged = cached && sameFeedSearchFields(source, cached.source);
      if (unchanged) {
        rows.push(cached.fields);
      } else {
        const fields: FeedSearchField[] = [];
        for (const field of source) {
          fields.push({ ...field, text: feedSearchText(field) });
          // ponytail: one field is atomic; a worker parser is needed to split huge individual bodies
          yield true;
        }
        cache.set(item.entry.card, { source, fields });
        rows.push(fields);
      }
    }
    yield false;
  }
  return rows;
}

// ponytail: specialised blocks use source text; exact ranges need renderer-aware indexing
const markdown = new Marked({
  renderer: {
    html: ({ text }) =>
      text
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;'),
  },
});

export function feedSearchText(field: FeedSearchField): string {
  if (field.format !== 'html' && field.format !== 'markdown')
    return normalizeFindText(field.text);
  const html =
    field.format === 'markdown'
      ? markdown.parse(stripRoutinePreamble(field.text), { async: false })
      : field.text;
  // Template contents are inert: indexing must not load images or execute markup
  const template = document.createElement('template');
  template.innerHTML = html;
  return readFindText(template.content).text;
}
